import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import {
  PiCalendarBlankLight,
  PiTimerLight,
  PiUsersLight,
  PiArrowUpLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiReceiptLight,
  PiHourglassLight,
} from "react-icons/pi";
import { IconType } from "react-icons";
import {
  OrgLicenseAccess,
  LicenseStatus,
  isInGrace,
  isReadOnly,
} from "../../../store/actions/PlanActions";
import { formatLicenseDate } from "./renewal";

const TIER_ICONS: Record<string, { icon: IconType; color: string }> = {
  starter: { icon: PiShieldCheckLight, color: "text-fg-strong" },
  professional: { icon: PiRocketLaunchLight, color: "text-fg-strong" },
  enterprise: { icon: PiCrownLight, color: "text-fg-strong" },
};

const getTierIcon = (planName: string | null) => {
  if (!planName) return { icon: PiPackageLight, color: "text-fg-strong" };
  const key = planName.toLowerCase();
  for (const [tier, cfg] of Object.entries(TIER_ICONS)) {
    if (key.includes(tier)) return cfg;
  }
  return { icon: PiPackageLight, color: "text-fg-strong" };
};

interface CurrentPlanBannerProps {
  access: OrgLicenseAccess | null;
  status: LicenseStatus | null;
  isOrgAdmin?: boolean;
  onChangePlan: () => void;
  onAddSeats?: () => void;
  onManageSeats?: () => void;
}

const CurrentPlanBanner: React.FC<CurrentPlanBannerProps> = ({
  access,
  status,
  isOrgAdmin = true,
  onChangePlan,
  onAddSeats,
  onManageSeats,
}) => {
  const { t, i18n } = useTranslation();

  if (!status) return null;

  /* The §4.3 dates. /status and /my-access both carry them; /status is the
     one this card is built from, /my-access fills a gap. Admin-only: they are
     the billing facts an admin acts on, not something a member can change. */
  const pick = (k: "trial_ends_at" | "payment_due_at" | "effective_end" | "grace_ends_at") =>
    status[k] ?? access?.[k] ?? null;
  const licenceDates = isOrgAdmin
    ? [
        { key: "trial_ends_at", label: t("Trial ends"), icon: PiTimerLight, value: pick("trial_ends_at") },
        { key: "payment_due_at", label: t("Payment due"), icon: PiReceiptLight, value: pick("payment_due_at") },
        { key: "effective_end", label: t("License ends"), icon: PiCalendarBlankLight, value: pick("effective_end") },
        { key: "grace_ends_at", label: t("Grace period ends"), icon: PiHourglassLight, value: pick("grace_ends_at") },
      ].filter((d) => !!d.value)
    : [];
  const readOnly = isReadOnly(status) || isReadOnly(access);
  const inGrace = !readOnly && (isInGrace(status) || isInGrace(access));

  const licenseInfo = access?.license_info;
  const planName = licenseInfo?.plan_display_name || status.display_name || status.base_plan || null;
  // Non-admin users should only see addons assigned to them
  const activeAddons = (!isOrgAdmin && licenseInfo?.user_addons)
    ? licenseInfo.user_addons
    : (licenseInfo?.active_addons || status.active_addons || []);
  const tierCfg = getTierIcon(planName || status.base_plan || null);
  const TierIcon = tierCfg.icon;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t("No expiry");
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="glass rounded-lg overflow-hidden flex flex-col">
      {/* Header. Wraps rather than squeezes: three buttons beside the name
          used to fold "PONS Founding (full access)" into one word per line. */}
      <div className="p-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <TierIcon className={`h-5 w-5 ${tierCfg.color} flex-shrink-0`} />
            <h4 className="font-medium text-fg-strong text-sm">
              {planName || t("No active plan")}
            </h4>
            {status.has_active_license && (
              <Badge color="green">
                {t("Active")}
              </Badge>
            )}
            {status.is_trial && (
              <Badge color="yellow">
                {t("Trial")}
              </Badge>
            )}
            {readOnly ? (
              <Badge color="red">
                {t("Read-only")}
              </Badge>
            ) : inGrace ? (
              <Badge color="yellow">
                {t("Grace period")}
              </Badge>
            ) : !status.has_active_license && (
              <Badge color="red">
                {t("Inactive")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-fg-muted mt-1.5">
            {status.has_active_license
              ? t("Your current subscription plan")
              : t("No active subscription")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {status.has_active_license && onManageSeats && (
            <Button
              variant="outline"
              size="small"
              onClick={onManageSeats}
              icon={<PiUsersLight />}
            >
              {t("Manage seats")}
            </Button>
          )}
          {status.has_active_license && !status.is_trial && onAddSeats && (
            <Button
              variant="outline"
              size="small"
              onClick={onAddSeats}
              icon={<PiUsersLight />}
            >
              {t("Add seats")}
            </Button>
          )}
          <Button
            variant="primary"
            size="small"
            onClick={onChangePlan}
            icon={<PiArrowUpLight />}
          >
            {status.has_active_license ? t("Change plan") : t("Get started")}
          </Button>
        </div>
      </div>

      {/* Details - only if active */}
      {status.has_active_license && (
        <div className="border-t border-hairline-soft px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Trial countdown */}
          {status.is_trial && status.trial_days_remaining !== null && (
            <div className="flex items-center gap-3">
              <PiTimerLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">{t("Trial ends in")}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {status.trial_days_remaining} {t("days")}
                </p>
              </div>
            </div>
          )}

          {/* Renewal / Expiry - superseded by the effective end when the
              backend sends it (it already accounts for trial and due date). */}
          {!licenceDates.some((d) => d.key === "effective_end") &&
            (status.expires_at || licenseInfo?.expires_at) && (
            <div className="flex items-center gap-3">
              <PiCalendarBlankLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">
                  {status.is_trial ? t("Trial ends") : t("Renews on")}
                </p>
                <p className="text-sm font-medium text-fg-strong">
                  {formatDate(status.expires_at || licenseInfo?.expires_at || null)}
                </p>
              </div>
            </div>
          )}

          {/* Seats */}
          {(status.seat_count > 0 || (licenseInfo?.seat_count ?? 0) > 0) && (
            <div className="flex items-center gap-3">
              <PiUsersLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">{t("Seats")}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {licenseInfo
                    ? `${licenseInfo.seats_used} / ${licenseInfo.seat_count}`
                    : `${status.user_count} / ${status.seat_count}`}
                </p>
              </div>
            </div>
          )}

          {/* Active add-ons */}
          {activeAddons.length > 0 && (
            <div className="col-span-2">
              <p className="text-xs text-fg-muted mb-1">{t("Add-ons")}</p>
              <div className="flex flex-wrap gap-1.5">
                {activeAddons.map((name: string, idx: number) => (
                  <Badge key={idx} color="gray">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Licence dates (admins): trial end, payment due, effective end, grace
          end - whichever the backend sends. Shown in grace and read-only too,
          when there is no active licence, because that is when they matter. */}
      {licenceDates.length > 0 && (
        <div
          className="border-t border-hairline-soft px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-6"
          data-testid="license-dates"
        >
          {licenceDates.map(({ key, label, icon: Icon, value }) => (
            <div key={key} className="flex items-center gap-3" data-testid={`license-date-${key}`}>
              <Icon className="h-4 w-4 text-fg-strong flex-shrink-0" />
              <div>
                <p className="text-xs text-fg-muted mb-1">{label}</p>
                <p className="text-sm font-medium text-fg-strong">
                  {formatLicenseDate(value, i18n?.language) || value}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrentPlanBanner;
